import { SeasonalSemanticColors, SeasonConfig } from '../types';
import { AvatarWreath } from './avatar-wreath';
import { BackgroundTint } from './background-tint';
import { FallingSnow } from './falling-snow';
import winterSceneBg from './images/background.png';
import WinterLogoDark from './images/logo-dark.svg';
import WinterLogoLight from './images/logo-light.svg';
import MorningLightMusic from './sounds/MorningLightMusic.mp3';

/**
 * Winter Holiday Theme - Semantic Color Palette
 *
 * Design intent:
 * - Deep navy backgrounds with subtle blue undertones (icy, wintry feel)
 * - Crimson red as the primary accent (festive holiday feel)
 * - Cool gray text colors for comfortable dark mode reading
 * - Inverted gray scale for proper dark theme support
 */
const winterSemanticColors: SeasonalSemanticColors = {
	// Primary accent - Holiday red
	primary: {
		fg: '#dc2626', // red-600
		fgStrong: '#b91c1c', // red-700
		fgStronger: '#991b1b', // red-800
		fgStrongest: '#7f1d1d', // red-900
		bg: '#dc2626',
		bgHover: '#b91c1c',
		bgActive: '#991b1b',
		bgAlpha: 'rgba(220, 38, 38, 0.12)',
		bgAlphaHover: 'rgba(220, 38, 38, 0.20)',
		bgAlphaActive: 'rgba(220, 38, 38, 0.28)',
	},

	// Backgrounds - Transparent canvas to show winter scene, solid surfaces for cards
	bg: {
		canvas: 'transparent', // Transparent to show background image
		surface: '#111827', // Cards, panels (gray-900 with blue)
		soft: '#1e293b', // Hover states, subtle emphasis (slate-800)
		subtle: '#1a2234', // Very subtle contrast
		topNav: '#0f172a', // Navigation bar (slate-900)
		inset: '#0d1424', // Inset areas, wells
	},

	// Text - Cool grays for dark mode
	text: {
		default: '#f1f5f9', // slate-100 - primary text
		soft: '#cbd5e1', // slate-300 - secondary text
		softer: '#94a3b8', // slate-400 - tertiary text
		softest: '#64748b', // slate-500 - disabled/hint text
		onColor: '#ffffff', // Text on colored backgrounds
		interactive: '#dc2626', // Links and interactive text (matches primary)
	},

	// Borders - Subtle definition in dark mode
	border: {
		default: '#334155', // slate-700 - standard borders
		soft: '#1e293b', // slate-800 - subtle borders
	},

	// Inverted gray scale for dark theme
	gray: {
		25: '#1e293b', // Was lightest, now darkest visible
		50: '#1a2234',
		100: '#253449',
		200: '#334155',
		300: '#475569',
		400: '#64748b',
		500: '#94a3b8',
		600: '#cbd5e1',
		700: '#e2e8f0',
		800: '#f1f5f9',
		900: '#f8fafc',
	},

	// Base color swaps for dark mode
	white: '#111827', // Card/surface backgrounds
	black: '#f8fafc', // Text and dark elements become light
};

/**
 * Winter/Holiday Theme Configuration
 * Defines all assets, text, colors, and components for the winter seasonal theme
 */
export const winterConfig: SeasonConfig = {
	// Theme identification
	themeClassName: 'winter-theme',
	toggleEmoji: '❄️',

	// Logo assets
	logoLight: WinterLogoLight, // Used in navbar (light background)
	logoDark: WinterLogoDark, // Used in modal (dark background)

	// Modal assets
	modalSound: MorningLightMusic,
	modalSoundVolume: 0.1,

	// Complete semantic color overrides
	semanticColors: winterSemanticColors,

	// Button text replacements - winter/holiday themed
	buttonTexts: ['❄️ Spread holiday cheer', '⛄ Send winter warmth', '🎁 Deliver appreciation', '✨ Share festive kudos', '🎅 Give a jolly shoutout'],

	// Modal decorative emojis - winter themed
	modalDecorations: [
		{ emoji: '🎄', top: '10px', left: '20px', fontSize: '40px', opacity: 0.2, rotation: -15 },
		{ emoji: '❄️', top: '30px', right: '30px', fontSize: '30px', opacity: 0.15, rotation: 15 },
		{ emoji: '⛄', bottom: '40px', left: '30px', fontSize: '25px', opacity: 0.15, rotation: -10 },
		{ emoji: '✨', top: '60px', left: '60%', fontSize: '20px', opacity: 0.1, rotation: 25 },
		{ emoji: '🎁', bottom: '60px', right: '40px', fontSize: '22px', opacity: 0.12, rotation: 12 },
	],

	// Modal colors - winter themed with blues and reds
	modalColors: {
		overlayBg: 'blackAlpha.800',
		contentBg: '#0a0f1a',
		contentBgImage: `linear-gradient(180deg, rgba(10, 15, 30, 0.85) 0%, rgba(15, 25, 50, 0.75) 50%, rgba(10, 15, 30, 0.9) 100%), url("${winterSceneBg}")`,
		contentBgSize: 'cover, 100% auto',
		contentBgPosition: 'center, center bottom',
		contentBgRepeat: 'no-repeat, no-repeat',
		borderColor: 'red.500',
		boxShadow: '0 20px 60px rgba(220, 38, 38, 0.5)',
		logoDropShadowColor: 'rgba(220, 38, 38, 0.6)', // Red glow for winter/holiday
		subtitleColor: 'blue.200',
		enableButtonBg: 'red.600',
		enableButtonHoverBg: 'red.500',
		enableButtonActiveBg: 'red.700',
		enableButtonBoxShadow: '0 4px 15px rgba(220, 38, 38, 0.4)',
		enableButtonHoverBoxShadow: '0 8px 25px rgba(220, 38, 38, 0.6)',
		cancelButtonColor: 'blue.300',
		cancelButtonHoverColor: 'blue.200',
		noticeBg: 'rgba(30, 58, 138, 0.5)',
		noticeBorderColor: 'blue.600',
		noticeTextColor: 'blue.200',
	},

	// Modal text content
	modalText: {
		subtitle: 'Celebrate the season with our festive winter theme! ❄️',
		features: [
			{
				emoji: '❄️',
				text: 'Gentle snowfall with beautiful snowflakes drifting across your screen',
			},
			{
				emoji: '🎄',
				text: 'Festive holiday colors with warm reds and cool blues throughout the interface',
			},
			{
				emoji: '🎁',
				text: 'Recognition you receive through Dec. 31st arrives gift-wrapped — unwrap to reveal!',
			},
		],
		endDateNotice: 'This festive theme will be available through the holiday season. Toggle it on/off anytime!',
		enableButtonText: 'Enable Winter Theme',
		cancelButtonText: 'Maybe Later',
	},

	// Visual effects components
	EffectsComponent: () => (
		<>
			<BackgroundTint />
			<FallingSnow />
			<AvatarWreath />
		</>
	),
};
